import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CalendarClock, CheckCircle2, Loader2, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Reusable reschedule modal for hosts and admins.
 *
 * ADMIN (`isAdmin === true`)
 *   → PATCH /bookings/{id}   (direct edit, server-validated).
 *
 * CLIENT (`isAdmin === false`)
 *   → POST /bookings/{id}/reschedule-request  (pedido para aprovação).
 *   Mostra alerta claro sobre os riscos (sem garantia de disponibilidade,
 *   depende de aprovação da LuxStay e da capacidade da equipa). Se já
 *   existir um pedido pendente, o formulário entra em modo "em análise"
 *   e só mostra o estado — sem permitir submeter outro.
 */
export default function RescheduleModal({ booking, isAdmin = false, onClose, onSaved }) {
  const [date, setDate] = useState(booking.scheduled_date);
  const [time, setTime] = useState(booking.scheduled_time);
  const [notes, setNotes] = useState(booking.notes || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null); // existing pending request (client only)
  const [checking, setChecking] = useState(!isAdmin);

  // For clients: check whether there's already a pending request for this booking
  useEffect(() => {
    let alive = true;
    if (isAdmin) { setChecking(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/bookings/${booking.id}/reschedule-request`);
        if (alive) setPending(data?.pending || null);
      } catch {
        /* silent — modal still usable */
      } finally {
        if (alive) setChecking(false);
      }
    })();
    return () => { alive = false; };
  }, [booking.id, isAdmin]);

  async function submit(e) {
    e.preventDefault();
    const changedDate = date !== booking.scheduled_date;
    const changedTime = time !== booking.scheduled_time;
    const changedNotes = notes !== (booking.notes || "");
    if (!changedDate && !changedTime && !changedNotes) {
      toast.message("Nada para alterar.");
      return;
    }

    setBusy(true);
    try {
      if (isAdmin) {
        const patch = {};
        if (changedDate) patch.scheduled_date = date;
        if (changedTime) patch.scheduled_time = time;
        if (changedNotes) patch.notes = notes;
        const { data } = await api.patch(`/bookings/${booking.id}`, patch);
        toast.success("Reserva atualizada.");
        onSaved?.(data.booking || data);
        onClose?.();
      } else {
        const payload = {};
        if (changedDate) payload.scheduled_date = date;
        if (changedTime) payload.scheduled_time = time;
        if (changedNotes) payload.notes = notes;
        if (reason.trim()) payload.reason = reason.trim();
        const { data } = await api.post(
          `/bookings/${booking.id}/reschedule-request`, payload,
        );
        toast.success("Pedido enviado. A administração vai analisar.");
        setPending(data?.request || null);
        onSaved?.(booking); // booking itself unchanged until approved
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <form onSubmit={submit}
              className="lux-card bg-[#041f1b] border border-[#1a4640] w-full max-w-md max-h-[90vh] overflow-auto pointer-events-auto"
              data-testid="reschedule-modal">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#1a4640] sticky top-0 bg-[#041f1b]">
            <div>
              <div className="eyebrow text-[#d4af37] flex items-center gap-2">
                <CalendarClock size={14} />
                {isAdmin ? "Alterar reserva" : "Pedir alteração"}
              </div>
              <div className="text-xs text-[#a0b2af] mt-1">
                {booking.apt_type} · {booking.scheduled_date} {booking.scheduled_time}
              </div>
            </div>
            <button type="button" onClick={onClose}
                    className="p-2 hover:text-[#d4af37] text-[#a0b2af]"
                    data-testid="reschedule-close">
              <X size={16} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {!isAdmin && checking && (
              <div className="text-xs text-[#a0b2af] p-3 border border-[#1a4640]">
                A verificar pedidos anteriores…
              </div>
            )}

            {!isAdmin && !checking && pending && (
              <div
                className="p-4 border border-[#d4af37]/50 bg-[#d4af37]/5 text-sm space-y-2"
                data-testid="reschedule-pending-banner"
              >
                <div className="flex items-center gap-2 text-[#d4af37] font-semibold">
                  <Clock /> Pedido em análise
                </div>
                <div className="text-[#fdfbf7] text-xs leading-relaxed">
                  Já existe um pedido pendente de aprovação para esta reserva.
                  Aguarde a decisão da administração. Será notificado por
                  email e na app assim que houver resposta.
                </div>
                {pending?.requested_changes && (
                  <div className="text-[11px] text-[#a0b2af] space-y-0.5 pt-1 border-t border-[#d4af37]/20 mt-2">
                    {pending.requested_changes.scheduled_date && (
                      <div>Nova data pedida: <span className="text-[#fdfbf7]">{pending.requested_changes.scheduled_date}</span></div>
                    )}
                    {pending.requested_changes.scheduled_time && (
                      <div>Nova hora pedida: <span className="text-[#fdfbf7]">{pending.requested_changes.scheduled_time}</span></div>
                    )}
                    {pending.reason && (
                      <div>Motivo: <span className="text-[#fdfbf7]">{pending.reason}</span></div>
                    )}
                  </div>
                )}
              </div>
            )}

            {!isAdmin && !checking && !pending && (
              <div
                className="p-4 border border-[#f59e0b]/50 bg-[#f59e0b]/5 text-xs leading-relaxed space-y-2"
                data-testid="reschedule-warning"
              >
                <div className="flex items-center gap-2 text-[#f59e0b] font-semibold text-sm">
                  <AlertTriangle size={14} /> Aviso importante
                </div>
                <ul className="list-disc list-inside space-y-1 text-[#fdfbf7]">
                  <li>Este pedido <b>não é confirmado automaticamente</b> — tem de ser aprovado pela administração.</li>
                  <li>A nova data/hora pode <b>não estar disponível</b> (capacidade da equipa, reservas concorrentes).</li>
                  <li>Enquanto o pedido estiver em análise, a reserva <b>mantém-se na data original</b>.</li>
                  <li>Se for rejeitado, ser-lhe-á comunicado o motivo e poderá submeter outro pedido.</li>
                </ul>
              </div>
            )}

            <div className="space-y-1">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Data pretendida</label>
              <input type="date" value={date} required
                     onChange={(e) => setDate(e.target.value)}
                     disabled={busy || (!isAdmin && !!pending)}
                     className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none disabled:opacity-50"
                     data-testid="reschedule-date" />
            </div>

            <div className="space-y-1">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Hora pretendida</label>
              <input type="time" value={time} required
                     onChange={(e) => setTime(e.target.value)}
                     disabled={busy || (!isAdmin && !!pending)}
                     className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none disabled:opacity-50"
                     data-testid="reschedule-time" />
            </div>

            <div className="space-y-1">
              <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Notas (opcional)</label>
              <textarea rows={3} value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={busy || (!isAdmin && !!pending)}
                        className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none disabled:opacity-50 resize-none"
                        data-testid="reschedule-notes" />
            </div>

            {!isAdmin && !pending && (
              <div className="space-y-1">
                <label className="text-xs uppercase tracking-widest text-[#a0b2af]">Motivo (opcional)</label>
                <textarea rows={2} value={reason} maxLength={500}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="Ex.: cliente final remarcou check-in para o próprio dia."
                          className="w-full bg-[#062c27] border border-[#1a4640] px-3 py-2.5 text-sm focus:border-[#d4af37] focus:outline-none resize-none"
                          data-testid="reschedule-reason" />
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#1a4640]">
            <button type="button" onClick={onClose} disabled={busy}
                    className="text-xs uppercase tracking-widest text-[#a0b2af] hover:text-[#d4af37] disabled:opacity-50"
                    data-testid="reschedule-cancel">
              {pending ? "Fechar" : "Cancelar"}
            </button>
            {!pending && (
              <button type="submit" disabled={busy || checking}
                      className="btn-gold text-xs px-5 py-2.5 uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-2"
                      data-testid="reschedule-save">
                {busy && <Loader2 size={12} className="animate-spin" />}
                {isAdmin
                  ? (busy ? "A guardar…" : "Guardar alteração")
                  : (busy ? "A enviar…" : "Enviar pedido")}
              </button>
            )}
            {!isAdmin && pending && (
              <span className="inline-flex items-center gap-2 text-xs text-[#d4af37]">
                <CheckCircle2 size={14} /> Pedido registado
              </span>
            )}
          </div>
        </form>
      </div>
    </>
  );
}

function Clock(props) {
  // tiny inline icon so we don't add yet another lucide import
  return <CalendarClock size={14} {...props} />;
}
